import React from "react";
import API from "@beardeddevops/react.api";
import Types from "constants/Types";
import Framework from "@beardeddevops/react.framework";
import {Button} from "reactstrap";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import moment from "moment-timezone";
import {Link} from "react-router-dom";
import UserRoles from "constants/UserRoles";

const reasonLabel = (reason) => {
	if (reason === "inactivity_2h") return "Inactivity";
	if (reason === "schedule_elapsed") return "Slot ended";
	return "Later onsite";
};

const triggerLabel = (row) => {
	if (Number(row.trigger_ticket) > 0) {
		return <Link to={`/tickets/${row.trigger_ticket}`}>#{row.trigger_fresh || row.trigger_ticket}</Link>;
	}
	return row.reason === "schedule_elapsed" ? "Slot ended, not ran" : "Last ticket / after 6pm";
};

const phoenixStamp = (value) => (
	value ? moment(value).tz("America/Phoenix").format("MM/DD/YYYY h:mm A") : ""
);

export default class MissingFinancialWorkflows extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			items: [],
			resolved: [],
			highlight: null,
			loading: false,
			resolvedLoading: false,
			resolvingPkey: null,
		};
	}

	componentDidMount() {
		this.loadRows();
		this.loadResolved();
		this.applyHighlightFromLocation();
	}

	componentDidUpdate() {
		const hash = this.props.history?.location?.hash || "";
		if (hash !== this._hash) {
			this._hash = hash;
			this.applyHighlightFromLocation();
		}
	}

	applyHighlightFromLocation = () => {
		const hash = (this.props.history?.location?.hash || "").replace(/^#/, "").replace(/^\?/, "");
		this._hash = this.props.history?.location?.hash || "";
		const params = new URLSearchParams(hash);
		const pkey = Number(params.get("pkey"));
		this.setState({highlight: pkey || null});
	};

	canResolve = () => {
		const roles = this.props.roles || [];
		return roles.some((role) => [UserRoles.Admin, UserRoles.HelpDesk].includes(role));
	};

	parseItems = (data) => {
		if (Array.isArray(data)) return data;
		if (Array.isArray(data?.items)) return data.items;
		if (Array.isArray(data?.response?.items)) return data.response.items;
		return [];
	};

	loadRows = async () => {
		this.setState({loading: true});
		try {
			const model = new API.BuildClass(Types.Missing_Financial_Workflows._name, "GET_ALL");
			model.limit = 500;
			model.offset = 0;
			const data = await model.submit(null, "GET_ALL");
			this.setState({loading: false, items: this.parseItems(data)});
		} catch (e) {
			this.setState({loading: false, items: []});
			API.Toast.error(e?.message || "Missing financials could not be loaded.");
		}
	};

	loadResolved = async () => {
		this.setState({resolvedLoading: true});
		try {
			const model = new API.BuildClass(Types.Missing_Financial_Workflows._name, "GET_RESOLVED");
			model.limit = 500;
			model.offset = 0;
			const data = await model.submit(null, "GET_RESOLVED");
			this.setState({resolvedLoading: false, resolved: this.parseItems(data)});
		} catch (e) {
			this.setState({resolvedLoading: false, resolved: []});
		}
	};

	refreshSession = () => {
		if (typeof this.props.onSessionCheck === "function") {
			this.props.onSessionCheck();
		}
		if (typeof this.props.reloadShell === "function") {
			this.props.reloadShell();
		}
	};

	resolve = async (pkey) => {
		if (this._resolving) {
			return;
		}
		this._resolving = true;
		this.setState({
			resolvingPkey: pkey,
			items: (this.state.items || []).filter((row) => row.pkey !== pkey),
		});
		try {
			const model = new API.BuildClass(Types.Missing_Financial_Workflows._name, "POST_RESOLVE");
			model.object = {pkey};
			const result = await model.submit(null, "POST_RESOLVE");
			if (typeof result === "string") {
				throw new Error(result);
			}
			if (result && result.success === false) {
				throw new Error(result.error || result.message || "Could not mark dealt with.");
			}
			await this.loadRows();
			await this.loadResolved();
			this.refreshSession();
		} catch (e) {
			API.Toast.error(e?.message || "Could not mark dealt with.");
			await this.loadRows();
			await this.loadResolved();
		} finally {
			this._resolving = false;
			this.setState({resolvingPkey: null});
		}
	};

	renderActiveTable() {
		const {items, loading, highlight} = this.state;
		return (
			<>
				<p>Today's scheduled tickets with no financial row or null labor. Opened/closed does not matter. Drops when labor is recorded, the ticket is cancelled/rescheduled, Helpdesk marks dealt with, or the next Phoenix day (then 7 Day Missing owns it). SDC stays here until labor is filled. Later-ticket fires when the next job goes onsite. A slot that ended without being ran also appears here. Two-hour inactivity is only for the last appointment of the day, or after 6pm Phoenix.</p>
				<table className="table table-sm table-striped">
					<thead>
					<tr>
						<th>Engineer</th>
						<th>Customer</th>
						<th>Missing ticket</th>
						<th>Trigger</th>
						<th>Reason</th>
						<th>First seen</th>
						<th></th>
					</tr>
					</thead>
					<tbody>
					{(items || []).map((row) => (
						<tr key={row.pkey} style={highlight === row.pkey ? {background: "#fff3cd"} : null}>
							<td>{row.engineer_name}</td>
							<td>{row.customer_name || "Customer"}</td>
							<td>
								{row.missing_ticket ? <Link to={`/tickets/${row.missing_ticket}`}>#{row.missing_fresh || row.missing_ticket}</Link> : "-"}
								<div className="text-muted">{phoenixStamp(row.missing_start)}</div>
							</td>
							<td>{triggerLabel(row)}</td>
							<td>{reasonLabel(row.reason)}</td>
							<td>{phoenixStamp(row.first_detected)}</td>
							<td>
								{this.canResolve() ? (
									<Button
										size="sm"
										color="success"
										disabled={!!this.state.resolvingPkey}
										onClick={() => this.resolve(row.pkey)}
									>
										Dealt with
									</Button>
								) : null}
							</td>
						</tr>
					))}
					</tbody>
				</table>
				{!loading && (!items || items.length < 1) && <p>No missing financials for today.</p>}
				<Framework.Elements.LoadingSpinner isLoading={loading}/>
			</>
		);
	}

	renderResolvedTable() {
		const {resolved, resolvedLoading} = this.state;
		return (
			<div style={{marginTop: 32}}>
				<h5>Dealt with (last 14 days)</h5>
				<p>Previous days included. Helpdesk/Admin name is who dismissed the Today Missing banner. System means labor was filled, the Phoenix day rolled, or the ticket was cancelled/rescheduled.</p>
				<table className="table table-sm table-striped">
					<thead>
					<tr>
						<th>Engineer</th>
						<th>Customer</th>
						<th>Missing ticket</th>
						<th>Reason</th>
						<th>Resolved at</th>
						<th>Resolved by</th>
					</tr>
					</thead>
					<tbody>
					{(resolved || []).map((row) => (
						<tr key={`resolved-${row.pkey}`}>
							<td>{row.engineer_name}</td>
							<td>{row.customer_name || "Customer"}</td>
							<td>
								{row.missing_ticket ? <Link to={`/tickets/${row.missing_ticket}`}>#{row.missing_fresh || row.missing_ticket}</Link> : "-"}
								<div className="text-muted">{phoenixStamp(row.missing_start)}</div>
							</td>
							<td>{reasonLabel(row.reason)}</td>
							<td>{phoenixStamp(row.resolved_at)}</td>
							<td>{row.resolved_by_name || "System"}</td>
						</tr>
					))}
					</tbody>
				</table>
				{!resolvedLoading && (!resolved || resolved.length < 1) && <p>No dismissals in the last 14 days.</p>}
				<Framework.Elements.LoadingSpinner isLoading={resolvedLoading}/>
			</div>
		);
	}

	render() {
		return (
			<Card>
				<CardContent>
					{this.renderActiveTable()}
					{this.renderResolvedTable()}
				</CardContent>
			</Card>
		);
	}
}
